'use client';

import { Container } from '@/components/container';

export default function ToS() {
  return (
    <section className="p-8 w-full mx-auto bg-gray-50 min-h-screen">
      <Container>
        <div className="py-8 flex flex-col gap-8 max-w-2xl mx-auto">
          <h1 className="font-bold text-3xl tracking-tight mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-700 mb-6">
            Thank you for choosing InnovationHUBSolutions. Our platform is
            designed to enable professionals to share and exchange knowledge
            about innovation and change management. By accessing or using our
            services, you agree to be bound by the following terms. If you do
            not agree to these Terms of Service, please discontinue your use of
            the platform.
          </p>

          <Section title="Acceptance of Terms">
            <p>
              These Terms of Service govern your access to and use of
              InnovationHUBSolutions, including any content, functionality, and
              services offered through the website. By using
              InnovationHUBSolutions, you accept these terms fully and
              acknowledge that you understand them. If at any time you do not
              agree to these terms, you must immediately stop using the
              platform.
            </p>
          </Section>

          <Section title="Content Ownership and License">
            <p>
              As a user, you retain ownership of any content you create or
              upload while using InnovationHUBSolutions. However, by posting
              content to the platform, you grant InnovationHUBSolutions an
              irrevocable, worldwide, royalty-free license to use, reproduce,
              modify, adapt, publish, translate, distribute, and monetize the
              content in any medium or format and to give access to such content
              to other users of our platform. InnovationHUBSolutions also
              reserves the right to sublicense the content. You represent that
              your content does not infringe upon the intellectual property or
              other rights of third parties.
            </p>
          </Section>

          <Section title="User Responsibility and Conduct">
            <p>
              You are solely responsible for any content you upload or share on
              InnovationHUBSolutions. This includes ensuring that your content
              complies with all applicable laws and does not violate the
              intellectual property or privacy rights of others.
              InnovationHUBSolutions will not monitor user-generated content for
              accuracy, legality, or decency and cannot be held responsible for
              such content. You agree to use the platform ethically and refrain
              from sharing content that is harmful, illegal, abusive, or
              otherwise objectionable.
            </p>
          </Section>

          <Section title="User Agreement on Content Quality">
            <h3 className="font-semibold text-lg mb-2">
              User Content Standards:
            </h3>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <b>Professionalism:</b> All content must be presented in a
                professional manner and should contribute constructively to the
                knowledge-sharing environment of the platform.
              </li>
              <li>
                <b>Relevance:</b> Content should be relevant to the themes of
                innovation and change management. Users are encouraged to ensure
                that their contributions are appropriate for the target
                audience.
              </li>
              <li>
                <b>Accuracy:</b> Users are responsible for providing accurate
                information and must not knowingly share false or misleading
                content. Users should cite credible sources where applicable.
              </li>
              <li>
                <b>Respectful Engagement:</b> All contributions should be
                respectful and considerate of diverse perspectives. Content that
                is harmful, abusive, or discriminatory will not be tolerated.
              </li>
              <li>
                <b>Compliance with Laws:</b> Users must ensure that their
                content complies with all applicable laws and regulations and
                does not infringe upon the rights of others, including
                intellectual property and privacy rights.
              </li>
              <li>
                <b>Confidentiality:</b> Users must not post any content that
                user is obligated to hold in confidence or that user reasonably
                believes is the confidential information of a third party.
              </li>
              <li>
                <b>Defamatory Material:</b> Users are prohibited from posting
                defamatory, offensive or threatening material.
              </li>
              <li>
                <b>Commercial Exploitation:</b> Users are prohibited from
                commercially exploiting the site, including selling, licensing,
                or renting it. Users are also prohibited from modifying, reverse
                engineering, or disassembling any part of the site.
              </li>
              <li>
                <b>Enforcement:</b> InnovationHUBSolutions reserves the right,
                but is not obligated, to review user-generated content and to
                remove or modify any content that does not meet these standards
                at its sole discretion.
              </li>
            </ul>
          </Section>

          <Section title="Moderation and Reporting Mechanism">
            <h3 className="font-semibold text-lg mb-2">
              Reporting Inappropriate Content:
            </h3>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <b>User Responsibility:</b> Users are encouraged to monitor
                content on the platform and report any material that they
                believe violates these Terms of Service or is otherwise
                inappropriate.
              </li>
              <li>
                <b>Reporting Process:</b> If you encounter content that you
                consider harmful, offensive, or otherwise inappropriate, please
                report it by following these steps:
                <ul className="list-decimal pl-6 mt-1">
                  <li>Navigate to the specific content in question.</li>
                  <li>
                    Click on the "Report" button (or equivalent) provided on the
                    platform.
                  </li>
                  <li>
                    Provide a brief description of the issue, including any
                    relevant details that will assist in the review process.
                  </li>
                </ul>
              </li>
              <li>
                <b>Review and Action:</b> InnovationHUBSolutions will review all
                reports submitted by users. While we strive to address reports
                promptly, we cannot guarantee the removal of any content. The
                decision to remove or act regarding reported content will be at
                the sole discretion of InnovationHUBSolutions.
              </li>
              <li>
                <b>No Guarantee of Monitoring:</b> Users acknowledge that while
                InnovationHUBSolutions encourages reporting of inappropriate
                content, the platform does not undertake an obligation to
                monitor user-generated content actively. InnovationHUBSolutions
                is not responsible for any content irrespective of whether it is
                reported or addressed in a timely manner.
              </li>
            </ul>
          </Section>

          <Section title="Limitation of Liability">
            <p>
              InnovationHUBSolutions is not liable for any direct, indirect,
              incidental, special, consequential, or punitive damages arising
              out of or relating to your use or interpretation of the platform.
              This includes, but is not limited to, any damages caused by
              user-generated content, third-party conduct, data loss,
              unauthorized access, or inability to use the platform. You
              understand that InnovationHUBSolutions does not guarantee
              uninterrupted service and may not be held responsible for any
              damages or losses related to outages or technical issues.
            </p>
          </Section>

          <Section title="No Contact Clause">
            <p>
              InnovationHUBSolutions does not provide support or respond to
              individual user complaints about content or other users on the
              platform. If you encounter issues or disputes related to other
              users or their content, you agree that it is your sole
              responsibility to manage those situations without seeking
              intervention from InnovationHUBSolutions. The platform is provided
              "as is," and you are responsible for ensuring that its use meets
              your needs and expectations.
            </p>
          </Section>

          <Section title="Content Removal">
            <p>
              InnovationHUBSolutions reserves the right to remove any content
              that violates these terms or that may be harmful to the platform
              or its users, at its sole discretion. However, we do not undertake
              an obligation to remove such content and are not liable for any
              delay or failure to remove it. Removed content may be retained by
              InnovationHUBSolutions in accordance with legal requirements or
              internal policies.
            </p>
          </Section>

          <Section title="Feedback and Suggestions">
            <h3 className="font-semibold text-lg mb-2">User Feedback:</h3>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <b>Encouragement of Feedback:</b> InnovationHUBSolutions
                welcomes feedback and suggestions from users regarding the
                platform's features, usability, and overall experience. Your
                insights are valuable in helping us enhance the platform to
                better meet user needs.
              </li>
              <li>
                <b>Submission Process:</b> Users can submit feedback and
                suggestions through the following methods:
                <ul className="list-decimal pl-6 mt-1">
                  <li>
                    Utilize the "Feedback" option available on the platform.
                  </li>
                  <li>
                    Send an email to the designated feedback address provided on
                    our website.
                  </li>
                </ul>
              </li>
              <li>
                <b>No Confidentiality:</b> Users acknowledge that any feedback
                or suggestions submitted to InnovationHUBSolutions shall be the
                sole and exclusive property of InnovationHUBSolutions, are not
                confidential and may be used by InnovationHUBSolutions in any
                way that InnovationHUBSolutions elects without any obligation to
                compensate the user and without any obligation to notify user.
                By submitting feedback, you assign to InnovationHUBSolutions all
                right, title and interest in and to the feedback including all
                copyrights, trademarks, patents and other intellectual property
                thereto.
              </li>
              <li>
                <b>No Guarantee of Action:</b> While we appreciate all feedback,
                InnovationHUBSolutions does not guarantee that any suggestions
                will be implemented. The decision to act upon or use user
                feedback will be at the sole discretion of
                InnovationHUBSolutions.
              </li>
            </ul>
          </Section>

          <Section title="Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless
              InnovationHUBSolutions and its affiliates from any claims,
              damages, losses, liabilities, costs, and expenses (including legal
              fees) arising from your use of the platform, your content
              (including any claims that your content infringes upon the
              intellectual property or other rights of a third party), or your
              violation of these Terms of Service.
            </p>
          </Section>

          <Section title="Disclaimer of Warranties">
            <p>
              InnovationHUBSolutions makes no representations or warranties with
              respect to the website, the content posted on it or the
              functioning of the platform. The website and platform are provided
              on an as is basis. INNOVATIONHUBSOLUTIONS EXPRESSLY DISCLAIMS ALL
              WARRANTIES INCLUDING ANY WARRANTY REGARDING INTELLECTUAL PROPERTY
              RIGHTS OR DATA POSTED ON ITS WEBSITE AND FURTHER DISCLAIMS ALL
              IMPLIED WARRANTIES INCLUDING THE WARRANTY OF MERCHANTABILITY AND
              FITNESS FOR A PARTICULAR PURPOSE.
            </p>
          </Section>

          <Section title="Changes to the Terms">
            <p>
              InnovationHUBSolutions reserves the right to modify these Terms of
              Service at any time. Changes will be posted on the website, and
              your continued use of the platform constitutes acceptance of the
              updated terms. You are responsible for reviewing these Terms of
              Service periodically to ensure that you are aware of any updates.
            </p>
          </Section>

          <Section title="Dispute Resolution and Governing Law">
            <p>
              Any disputes arising from these Terms of Service or your use of
              InnovationHUBSolutions will be resolved through binding
              arbitration, governed by the laws of New York. By agreeing to
              these terms, you waive the right to pursue claims in court and
              agree to resolve any disputes through arbitration.
            </p>
          </Section>

          <Section title="Termination">
            <p>
              InnovationHUBSolutions may terminate or suspend your account and
              access to the platform at any time, without notice or liability,
              for any reason, including breach of these Terms of Service. Upon
              termination, you must cease all use of the platform.
            </p>
          </Section>

          <Section title="Conclusion">
            <p>
              By using InnovationHUBSolutions, you acknowledge that you have
              read, understood, and agree to be bound by these Terms of Service.
              If you do not agree to these terms, please discontinue use of the
              platform immediately.
            </p>
          </Section>
        </div>
      </Container>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h2 className="font-semibold text-xl mb-2 text-gray-900">{title}</h2>
      <div className="text-gray-700 space-y-2">{children}</div>
    </section>
  );
}
